import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Check, AlertTriangle } from "lucide-react";
import { verifyMagicLink } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * /auth/verify — landing page for magic-link clicks.
 * Reads ?token=&email= from the URL, verifies with the backend, and
 * routes the user to their intended destination (return_url) once the
 * session cookie is set.
 */
export default function AuthVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "verifying", message: "" });

  useEffect(() => {
    const token = params.get("token");
    const email = params.get("email");
    if (!token || !email) {
      setState({ status: "error", message: "This sign-in link is missing information. Request a fresh one." });
      return;
    }
    let alive = true;
    (async () => {
      try {
        const res = await verifyMagicLink({ token, email });
        if (!alive) return;
        await refresh();
        setState({ status: "success", message: "Signed in! Redirecting…" });
        const dest = res?.return_url || "/";
        setTimeout(() => navigate(dest, { replace: true }), 900);
      } catch (err) {
        if (!alive) return;
        const detail = err?.response?.data?.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : detail?.message || "This sign-in link is invalid or has expired. Please request a new one.";
        setState({ status: "error", message: msg });
      }
    })();
    return () => { alive = false; };
  }, [params, navigate, refresh]);

  return (
    <div className="min-h-screen bg-paper text-ink flex items-center justify-center px-4" data-testid="auth-verify-page">
      <div className="w-full max-w-md bg-paper border-2 border-ink shadow-[8px_8px_0_#1A1C20] p-8">
        <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-press-red mb-6">
          ✦ TimeCapsule 24 · Sign In
        </div>

        {state.status === "verifying" && (
          <>
            <div className="mx-auto w-16 h-16 border-2 border-ink bg-paper-soft flex items-center justify-center mb-5">
              <Loader2 size={28} className="text-press-red animate-spin" />
            </div>
            <h1 className="font-headline uppercase font-black text-3xl leading-[0.95] tracking-tighter">
              Verifying…
            </h1>
            <p className="mt-3 font-body italic text-ink-muted">
              One moment while we unlock your account.
            </p>
          </>
        )}

        {state.status === "success" && (
          <>
            <div className="mx-auto w-16 h-16 border-2 border-ink bg-paper-soft flex items-center justify-center mb-5">
              <Check size={28} className="text-press-red" />
            </div>
            <h1 className="font-headline uppercase font-black text-3xl leading-[0.95] tracking-tighter">
              You&apos;re in.
            </h1>
            <p className="mt-3 font-body italic text-ink-muted">{state.message}</p>
          </>
        )}

        {state.status === "error" && (
          <>
            <div className="mx-auto w-16 h-16 border-2 border-ink bg-paper-soft flex items-center justify-center mb-5">
              <AlertTriangle size={28} className="text-press-red" />
            </div>
            <h1 className="font-headline uppercase font-black text-3xl leading-[0.95] tracking-tighter">
              Link expired.
            </h1>
            <p className="mt-3 font-body italic text-ink-muted">{state.message}</p>
            <button
              onClick={() => navigate("/", { replace: true })}
              className="mt-6 w-full bg-press-red text-paper border-2 border-press-red px-5 py-3 font-mono uppercase tracking-widest text-xs hover:bg-ink hover:border-ink"
              data-testid="auth-verify-back-btn"
            >
              Back to TimeCapsule 24 →
            </button>
          </>
        )}
      </div>
    </div>
  );
}
